import { useCallback, useMemo, useState } from 'react';
import {
  FilterValueConfig,
  FilterValueService,
} from '@/service/FilterValueService';
import { FilterValueRepository } from '@/repository/FilterValueRepository';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition';
import {
  FilterSelections,
  RelationalOperator,
} from '@lambda-layer/types/filter';
import log from 'loglevel';

export const useFilterValues = (listType: string) => {
  // Memoize the service instance to prevent recreation on every render
  const filterValueService = useMemo(() => {
    const repository = new FilterValueRepository();
    return new FilterValueService(repository);
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache the default values using React's built-in useMemo
  const getCachedDefaultValues = useMemo(() => {
    if (!listType) return Promise.resolve([]);

    log.debug(`Fetching default values for listType: ${listType}`);
    return filterValueService.getFilterValuesForListType(listType);
  }, [listType, filterValueService]);

  /**
   * Generates default filter selections
   */
  const generateDefaultSelections = useCallback(
    async (
      filterDefinitions: FilterDefinition[]
    ): Promise<FilterSelections> => {
      if (!listType || filterDefinitions.length === 0) {
        return {};
      }

      try {
        setLoading(true);
        setError(null);

        // Use the cached promise from useMemo
        const defaultValues = await getCachedDefaultValues;

        log.debug(`Got default values for ${listType}:`, defaultValues);

        const selections: FilterSelections = {};
        const validFieldIds = new Set(filterDefinitions.map(def => def.id));

        defaultValues.forEach((defaultValue: FilterValueConfig) => {
          // Only apply defaults for fields that exist in the filter definitions
          if (validFieldIds.has(defaultValue.fieldId)) {
            // Parse the relational operator string to the enum value
            const relationalOperator =
              Object.values(RelationalOperator).find(
                op => op === defaultValue.relationalOperator
              ) || RelationalOperator.EQUAL;

            selections[defaultValue.fieldId] = {
              relationalOperator,
              value: defaultValue.value || [],
            };
            log.debug(
              `Applied default for field ${defaultValue.fieldId}:`,
              defaultValue.value
            );
          } else {
            log.warn(
              `No matching filter definition found for fieldId: ${defaultValue.fieldId}`
            );
          }
        });

        log.debug(`Generated default selections for ${listType}`, selections);
        return selections;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : JSON.stringify(err);
        log.error(
          `Failed to generate default selections: ${errorMessage}`,
          err
        );
        setError(errorMessage);
        return {};
      } finally {
        setLoading(false);
      }
    },
    [listType, getCachedDefaultValues]
  );

  /**
   * Gets default configurations for a specific field
   */
  const getDefaultsForField = useCallback(
    async (fieldId: string): Promise<FilterValueConfig[]> => {
      if (!listType || !fieldId) {
        return [];
      }

      try {
        return await filterValueService.getFilterValuesForField(
          listType,
          fieldId
        );
      } catch (err) {
        log.error(`Failed to get defaults for field ${fieldId}:`, err);
        return [];
      }
    },
    [listType, filterValueService]
  );

  return {
    generateDefaultSelections,
    getDefaultsForField,
    loading,
    error,
  };
};
